/**
 * Public "set your password" page for the admin invite flow.
 *
 * Reached from the emailed registration link (/set-password?token=...). Validates
 * the token, lets the invited user choose a password, and activates the account.
 * There is no other way to register — without a valid token this page cannot set
 * a password.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card, Spinner } from 'react-bootstrap';
import Logo from '../components/common/Logo';
import authService from '../services/authService';

function extractErrorMessage(error: unknown, fallback: string): string {
    if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: unknown } } };
        const detail = axiosError.response?.data?.detail;
        if (typeof detail === 'string') return detail;
    }
    return fallback;
}

const SetPasswordPage: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');

    const [checking, setChecking] = useState(true);
    const [tokenError, setTokenError] = useState<string | null>(null);
    const [username, setUsername] = useState<string>('');

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [validated, setValidated] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    // Validate the token up front so we can greet the user and catch dead links.
    useEffect(() => {
        let active = true;
        if (!token) {
            setTokenError('This registration link is missing its token.');
            setChecking(false);
            return;
        }
        authService
            .validateRegistration(token)
            .then((info) => {
                if (active) setUsername(info.username);
            })
            .catch((err) => {
                if (active)
                    setTokenError(
                        extractErrorMessage(
                            err,
                            'This registration link is invalid or has expired.'
                        )
                    );
            })
            .finally(() => {
                if (active) setChecking(false);
            });
        return () => {
            active = false;
        };
    }, [token]);

    const passwordsMatch = password === confirm;

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const form = event.currentTarget;
        if (form.checkValidity() === false || !passwordsMatch) {
            event.stopPropagation();
            setValidated(true);
            return;
        }
        setValidated(true);
        setSubmitError(null);
        setSubmitting(true);
        try {
            await authService.setPassword(token as string, password);
            setSuccess(true);
        } catch (err) {
            setSubmitError(
                extractErrorMessage(
                    err,
                    'Could not set your password. The link may have expired.'
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Container fluid className="vh-100 d-flex align-items-center justify-content-center bg-brand-dark">
            <Row className="w-100 justify-content-center">
                <Col xs={12} sm={10} md={8} lg={6} xl={4}>
                    <Card className="shadow border-0">
                        <Card.Body className="p-4">
                            <div className="text-center mb-4">
                                <Logo variant="stacked-tagline" maxHeight={112} className="mb-3" />
                                <p className="ft-eyebrow mb-0">Set your password</p>
                            </div>

                            {checking && (
                                <div className="text-center py-4">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Checking link...</span>
                                    </Spinner>
                                </div>
                            )}

                            {!checking && tokenError && (
                                <>
                                    <Alert variant="danger">{tokenError}</Alert>
                                    <div className="d-grid">
                                        <Button variant="secondary" onClick={() => navigate('/login')}>
                                            Back to sign in
                                        </Button>
                                    </div>
                                </>
                            )}

                            {!checking && !tokenError && success && (
                                <>
                                    <Alert variant="success">
                                        Your password has been set. You can now sign in.
                                    </Alert>
                                    <div className="d-grid">
                                        <Button variant="primary" onClick={() => navigate('/login')}>
                                            Go to sign in
                                        </Button>
                                    </div>
                                </>
                            )}

                            {!checking && !tokenError && !success && (
                                <>
                                    {username && (
                                        <p className="text-center mb-4">
                                            Welcome, <strong>{username}</strong>. Choose a password
                                            to activate your account.
                                        </p>
                                    )}

                                    {submitError && (
                                        <Alert
                                            variant="danger"
                                            dismissible
                                            onClose={() => setSubmitError(null)}
                                        >
                                            {submitError}
                                        </Alert>
                                    )}

                                    <Form noValidate validated={validated} onSubmit={handleSubmit}>
                                        <Form.Group className="mb-3" controlId="newPassword">
                                            <Form.Label>New password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="At least 8 characters"
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                minLength={8}
                                                maxLength={128}
                                                disabled={submitting}
                                                autoComplete="new-password"
                                                autoFocus
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Password must be at least 8 characters.
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <Form.Group className="mb-4" controlId="confirmPassword">
                                            <Form.Label>Confirm password</Form.Label>
                                            <Form.Control
                                                type="password"
                                                placeholder="Re-enter your password"
                                                value={confirm}
                                                onChange={(e) => setConfirm(e.target.value)}
                                                required
                                                disabled={submitting}
                                                autoComplete="new-password"
                                                isInvalid={validated && !passwordsMatch}
                                            />
                                            <Form.Control.Feedback type="invalid">
                                                Passwords do not match.
                                            </Form.Control.Feedback>
                                        </Form.Group>

                                        <div className="d-grid">
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                size="lg"
                                                disabled={submitting}
                                            >
                                                {submitting ? (
                                                    <>
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                            className="me-2"
                                                        />
                                                        Setting password...
                                                    </>
                                                ) : (
                                                    'Set password'
                                                )}
                                            </Button>
                                        </div>
                                    </Form>
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default SetPasswordPage;
